import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Image, Plus, Upload, Trash2, AlertCircle, Radio } from 'lucide-react';
import { api, BACKEND_URL } from '@/api';
import { resolveMediaUrl, formatDate, formatTime } from '@/utils/helpers';
import StreamPanel from './StreamPanel';
import { MediaPickerDialog } from './AdminShared';
import { SportIcon, toLocalDatetimeInput, normalizeYouTubeUrl } from './adminGameHelpers';
// Re-export AdminFixtures for backward compatibility
export { AdminFixtures } from './AdminFixtures';

const AdminGames = ({ games, teams, leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [formData, setFormData] = useState({
    league_id: '', home_team_id: '', away_team_id: '', scheduled_time: '', status: 'scheduled',
    home_score: '', away_score: '', thumbnail: '', is_featured: false, is_paywall: false, ppv_price: null, access_type: 'free', required_plan_ids: [], background_image_url: '', featured_description: '', hero_zoom: 100, hero_position_x: 50, hero_position_y: 50, score_feed_url: '', replay_url: '', video_sources: []
  });
  const [newSource, setNewSource] = useState({ type: 'youtube', url: '', is_free: true, embed_url: '' });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [channels, setChannels] = useState([]);
  const [assigningChannel, setAssigningChannel] = useState(false);
  const [subPlans, setSubPlans] = useState([]);

  // Load channels and subscription plans
  useEffect(() => {
    const loadChannels = async () => {
      try {
        const res = await api.getChannels();
        setChannels(res.data);
      } catch { /* silent */ }
    };
    const loadPlans = async () => {
      try {
        const res = await api.getPlans();
        setSubPlans(res.data.plans || []);
      } catch { /* silent */ }
    };
    loadChannels();
    loadPlans();
  }, []);

  // Helper to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-500 text-white';
      case 'finished': return 'bg-gray-600 text-white';
      case 'scheduled': return 'bg-sky-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Handle thumbnail file upload
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, or WebP file');
      return;
    }
    
    setUploadingThumbnail(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', 'Match thumbnail');
      uploadFormData.append('tags', 'thumbnail,match');
      
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });
      const data = await res.json();
      
      const thumbnailUrl = resolveMediaUrl(data.url);
      setFormData({ ...formData, thumbnail: thumbnailUrl });
    } catch (error) {
      alert('Error uploading thumbnail');
      console.error(error);
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Normalize YouTube URLs in video sources
      const normalizedSources = formData.video_sources.map(s => ({
        ...s,
        url: s.type === 'youtube' ? normalizeYouTubeUrl(s.url) : s.url
      }));
      const data = {
        ...formData,
        home_score: formData.home_score !== '' ? parseInt(formData.home_score) : null,
        away_score: formData.away_score !== '' ? parseInt(formData.away_score) : null,
        scheduled_time: new Date(formData.scheduled_time).toISOString(),
        replay_url: formData.replay_url || null,
        is_paywall: formData.access_type !== 'free',
        ppv_price: formData.ppv_price || null,
        access_type: formData.access_type || 'free',
        required_plan_ids: formData.required_plan_ids || [],
        video_sources: normalizedSources
      };
      if (editingGame) {
        await api.updateGame(editingGame.id, data);
      } else {
        await api.createGame(data);
      }
      closeForm();
      onRefresh();
    } catch (error) {
      console.error(error);
      alert('Error saving game');
    }
  };

  const resetForm = () => {
    setEditingGame(null);
    setFormData({
      league_id: '', home_team_id: '', away_team_id: '', scheduled_time: '', status: 'scheduled',
      home_score: '', away_score: '', thumbnail: '', is_featured: false, is_paywall: false, ppv_price: null, access_type: 'free', required_plan_ids: [], background_image_url: '', featured_description: '', hero_zoom: 100, hero_position_x: 50, hero_position_y: 50, score_feed_url: '', replay_url: '', video_sources: []
    });
  };
  
  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      ...game,
      scheduled_time: toLocalDatetimeInput(game.scheduled_time),
      home_score: game.home_score ?? '',
      away_score: game.away_score ?? '',
      replay_url: game.replay_url || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this game?')) {
      try {
        await api.deleteGame(id);
        onRefresh();
      } catch (error) {
        alert('Error deleting game');
      }
    }
  };

  const addVideoSource = () => {
    if (newSource.url) {
      setFormData({
        ...formData,
        video_sources: [...formData.video_sources, { ...newSource }]
      });
      setNewSource({ type: 'youtube', url: '', is_free: true, embed_url: '' });
    }
  };

  const removeVideoSource = (index) => {
    setFormData({
      ...formData,
      video_sources: formData.video_sources.filter((_, i) => i !== index)
    });
  };

  const getTeamsForLeague = (leagueId) => teams.filter(t => 
    t.league_ids?.includes(leagueId) || t.league_id === leagueId
  );
  const getSportForLeague = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return sports.find(s => s.id === league?.sport_id);
  };

  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const csvInputRef = React.useRef(null);
  const thumbInputRef = React.useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [thumbFiles, setThumbFiles] = useState([]);

  const handleCsvImport = async () => {
    if (!csvFile) return alert('Please select a CSV file');
    setImporting(true);
    setImportResult(null);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('csv_file', csvFile);
      thumbFiles.forEach(f => formDataUpload.append('thumbnails', f));
      
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/games/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      const data = await res.json();
      setImportResult(data);
      if (data.created > 0) onRefresh();
    } catch (error) {
      setImportResult({ errors: [error.message] });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem('adminToken');
    const res = await fetch(`${BACKEND_URL}/api/admin/games/csv-template`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'games_import_template.csv'; a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Games ({games.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(!showImport)} className="border-sky-700 text-sky-300 hover:bg-sky-900/30">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button onClick={() => { setShowForm(true); resetForm(); }} className="bg-sky-300 hover:bg-sky-400">
            <Plus className="w-4 h-4 mr-2" /> Add Game
          </Button>
        </div>
      </div>

      {/* CSV Import Panel */}
      {showImport && (
        <Card className="bg-gray-800/50 border-amber-700/30">
          <CardHeader>
            <CardTitle className="text-white">Mass Import Games from CSV</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="border-gray-600 text-gray-300 hover:bg-gray-700" size="sm">
                Download CSV Template
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">CSV File</Label>
                <Input type="file" accept=".csv" ref={csvInputRef}
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Thumbnails Folder (select all images)</Label>
                <Input type="file" accept="image/*" multiple ref={thumbInputRef}
                  onChange={(e) => setThumbFiles(Array.from(e.target.files))}
                  className="bg-gray-700 border-gray-600 text-white" />
                {thumbFiles.length > 0 && <p className="text-xs text-gray-400 mt-1">{thumbFiles.length} image(s) selected</p>}
              </div>
            </div>
            <p className="text-xs text-gray-400">
              CSV columns: home_team, away_team, league, sport, scheduled_time, stream_url, stream_type, replay_url, thumbnail_filename, is_featured, featured_description.
              YouTube URLs are auto-normalized. Teams and leagues are auto-created if they don't exist.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleCsvImport} disabled={importing || !csvFile} className="bg-amber-500 hover:bg-amber-600 text-black">
                {importing ? 'Importing...' : 'Import Games'}
              </Button>
              <Button variant="outline" onClick={() => { setShowImport(false); setImportResult(null); setCsvFile(null); setThumbFiles([]); }}
                className="border-gray-600 text-gray-300">Cancel</Button>
            </div>
            {importResult && (
              <div className="bg-gray-900 rounded-lg p-4 mt-2 text-sm">
                {importResult.created > 0 && <p className="text-green-400">Created {importResult.created} game(s)</p>}
                {importResult.teams_created?.length > 0 && <p className="text-sky-300">New teams: {importResult.teams_created.join(', ')}</p>}
                {importResult.leagues_created?.length > 0 && <p className="text-sky-300">New: {importResult.leagues_created.join(', ')}</p>}
                {importResult.errors?.length > 0 && (
                  <div className="text-red-400 mt-2">
                    <p className="font-semibold">Errors:</p>
                    {importResult.errors.map((err, i) => <p key={`err-${i}`}>{err}</p>)}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingGame ? 'Edit Game' : 'Add New Game'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">League</Label>
                  <Select value={formData.league_id} onValueChange={(v) => setFormData({ ...formData, league_id: v, home_team_id: '', away_team_id: '' })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select League" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {leagues.map(league => {
                        const sport = getSportForLeague(league.id);
                        return (
                          <SelectItem key={league.id} value={league.id} className="text-white hover:bg-gray-700">
                            <span className="flex items-center gap-1.5"><SportIcon sport={sport} className="w-3.5 h-3.5 inline" /> {league.name}</span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Scheduled Time</Label>
                  <Input 
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Home Team</Label>
                  {formData.league_id && getTeamsForLeague(formData.league_id).length === 0 ? (
                    <div className="flex items-center gap-2 p-2 bg-amber-900/20 border border-amber-700/30 rounded-md text-amber-300 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" /> No teams assigned to this league. Add teams in the Teams tab first.
                    </div>
                  ) : (
                    <Select value={formData.home_team_id} onValueChange={(v) => setFormData({ ...formData, home_team_id: v })} disabled={!formData.league_id}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select Home Team" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {getTeamsForLeague(formData.league_id).map(team => (
                          <SelectItem key={team.id} value={team.id} className="text-white hover:bg-gray-700">
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Away Team</Label>
                  {formData.league_id && getTeamsForLeague(formData.league_id).length === 0 ? (
                    <div className="flex items-center gap-2 p-2 bg-amber-900/20 border border-amber-700/30 rounded-md text-amber-300 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" /> No teams assigned to this league.
                    </div>
                  ) : (
                    <Select value={formData.away_team_id} onValueChange={(v) => setFormData({ ...formData, away_team_id: v })} disabled={!formData.league_id}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select Away Team" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {getTeamsForLeague(formData.league_id).map(team => (
                          <SelectItem key={team.id} value={team.id} className="text-white hover:bg-gray-700">
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="scheduled" className="text-white hover:bg-gray-700">Scheduled</SelectItem>
                      <SelectItem value="live" className="text-white hover:bg-gray-700">Live</SelectItem>
                      <SelectItem value="finished" className="text-white hover:bg-gray-700">Finished</SelectItem>
                      <SelectItem value="postponed" className="text-white hover:bg-gray-700">Postponed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Match Thumbnail</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.thumbnail}
                      onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                      placeholder="URL or upload below"
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                        onChange={handleThumbnailUpload}
                        className="hidden"
                      />
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploadingThumbnail ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                        <Upload className="w-4 h-4" /> {uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}
                      </span>
                    </label>
                    {formData.thumbnail && (
                      <img 
                        src={resolveMediaUrl(formData.thumbnail)} 
                        alt="Thumbnail preview" 
                        className="h-16 w-24 object-cover rounded"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Home Score</Label>
                  <Input 
                    type="number"
                    value={formData.home_score}
                    onChange={(e) => setFormData({ ...formData, home_score: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="-"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Away Score</Label>
                  <Input 
                    type="number"
                    value={formData.away_score}
                    onChange={(e) => setFormData({ ...formData, away_score: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="-"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="rounded"
                />
                <Label className="text-gray-300">Featured Game (shows in hero carousel)</Label>
              </div>

              {formData.is_featured && (
                <div className="space-y-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                    <Image className="w-4 h-4" />
                    Hero Image Required for Featured Games
                  </div>
                  <p className="text-gray-400 text-xs">
                    Upload a high-quality, wide cinematic image (recommended: 1920x1080) for the homepage hero carousel. Without a hero image, a gradient background will be used.
                  </p>
                  <div>
                    <Label className="text-gray-300">Hero Background Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        value={formData.background_image_url || ''}
                        onChange={(e) => setFormData({ ...formData, background_image_url: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white flex-1"
                        placeholder="Background image URL for hero carousel"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                        onClick={() => setShowMediaPicker(true)}
                      >
                        <Image className="w-4 h-4 mr-2" /> Browse
                      </Button>
                    </div>
                    {formData.background_image_url && (
                      <div className="mt-2 relative">
                        <div className="overflow-hidden rounded max-w-md h-32 bg-gray-900">
                          <img 
                            src={resolveMediaUrl(formData.background_image_url)} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                            style={{
                              transform: `scale(${(formData.hero_zoom || 100) / 100})`,
                              transformOrigin: `${formData.hero_position_x || 50}% ${formData.hero_position_y || 50}%`,
                              objectPosition: `${formData.hero_position_x || 50}% ${formData.hero_position_y || 50}%`
                            }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Zoom & Position Controls */}
                  {formData.background_image_url && (
                    <div className="pt-3 border-t border-amber-500/20">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-300">Image Zoom & Position</Label>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="ghost" 
                          className="text-gray-400 hover:text-white text-xs"
                          onClick={() => setFormData({ ...formData, hero_zoom: 100, hero_position_x: 50, hero_position_y: 50 })}
                        >
                          Reset to Default
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">Zoom: {formData.hero_zoom || 100}%</Label>
                          <input
                            type="range"
                            min="100"
                            max="200"
                            step="5"
                            value={formData.hero_zoom || 100}
                            onChange={(e) => setFormData({ ...formData, hero_zoom: parseInt(e.target.value) })}
                            className="w-full accent-amber-500"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">Horizontal: {formData.hero_position_x || 50}%</Label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={formData.hero_position_x || 50}
                            onChange={(e) => setFormData({ ...formData, hero_position_x: parseInt(e.target.value) })}
                            className="w-full accent-amber-500"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">Vertical: {formData.hero_position_y || 50}%</Label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={formData.hero_position_y || 50}
                            onChange={(e) => setFormData({ ...formData, hero_position_y: parseInt(e.target.value) })}
                            className="w-full accent-amber-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Featured Match Write-up */}
                  <div className="pt-3 border-t border-amber-500/20">
                    <Label className="text-gray-300">Match Description (Info Overlay)</Label>
                    <p className="text-gray-500 text-xs mb-2">
                      Write a brief description about the match. This appears when viewers click "More Info" - similar to cable TV program info.
                    </p>
                    <textarea
                      value={formData.featured_description || ''}
                      onChange={(e) => setFormData({ ...formData, featured_description: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 min-h-[100px] text-sm"
                      placeholder="e.g., The defending champions face their cross-town rivals in this highly anticipated clash. Both teams are undefeated this season, making this a must-watch matchup that could determine playoff positioning..."
                    />
                    <p className="text-gray-500 text-xs mt-1">{(formData.featured_description || '').length}/500 characters</p>
                  </div>
                </div>
              )}

              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, background_image_url: url })}
              />

              {/* Score Feed URL for live updates */}
              <div>
                <Label className="text-gray-300">Live Score Feed URL</Label>
                <Input 
                  value={formData.score_feed_url || ''}
                  onChange={(e) => setFormData({ ...formData, score_feed_url: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="https://sports-stream-hub-19.preview.emergentagent.com/api/feed/text/..."
                />
                <p className="text-gray-500 text-xs mt-1">URL to fetch live score/minute text during the game</p>
              </div>

              {/* Replay URL */}
              <div>
                <Label className="text-gray-300">Replay URL (optional)</Label>
                <Input 
                  value={formData.replay_url || ''}
                  onChange={(e) => setFormData({ ...formData, replay_url: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="https://www.uscreen.tv/replay/..."
                />
                <p className="text-gray-500 text-xs mt-1">When set, the shareable link will redirect here instead of the live stream URL</p>
              </div>

              {/* Video Sources */}
              <div className="border border-gray-700 rounded-lg p-4">
                <Label className="text-gray-300 text-lg mb-4 block">Video Sources</Label>
                {formData.video_sources.map((source, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2 p-2 bg-gray-700/50 rounded">
                    <Badge className={source.is_free ? 'bg-green-500' : 'bg-sky-300'}>
                      {source.type.toUpperCase()} - {source.is_free ? 'FREE' : 'PLUS'}
                    </Badge>
                    <span className="text-gray-300 flex-1 truncate">{source.url}</span>
                    <Button size="sm" variant="destructive" onClick={() => removeVideoSource(index)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  <Select value={newSource.type} onValueChange={(v) => setNewSource({ ...newSource, type: v })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="youtube" className="text-white hover:bg-gray-700">YouTube</SelectItem>
                      <SelectItem value="uscreen" className="text-white hover:bg-gray-700">Plus</SelectItem>
                      <SelectItem value="nbc_sports" className="text-white hover:bg-gray-700">NBC Sports</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    value={newSource.url}
                    onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white col-span-2"
                    placeholder="Video URL"
                  />
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={newSource.is_free}
                      onChange={(e) => setNewSource({ ...newSource, is_free: e.target.checked })}
                    />
                    <span className="text-gray-300 text-sm">Free</span>
                    <Button type="button" size="sm" onClick={addVideoSource} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Access Control */}
              <div className="border border-gray-700 rounded-lg p-4">
                <Label className="text-gray-300 text-lg mb-3 block">Access Control</Label>
                <div className="space-y-3">
                  {/* Access Type */}
                  <div>
                    <Label className="text-gray-400 text-sm mb-1 block">Access Type</Label>
                    <select
                      value={formData.access_type || 'free'}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, access_type: val, is_paywall: val !== 'free' });
                      }}
                      className="bg-gray-700 border-gray-600 text-white rounded p-2 text-sm w-full"
                      data-testid="game-access-type"
                    >
                      <option value="free">Free to Access</option>
                      <option value="subscription">Subscription Required</option>
                      <option value="ppv">Pay-Per-View</option>
                    </select>
                  </div>

                  {/* Plan Selection (for subscription type) */}
                  {(formData.access_type === 'subscription') && (
                    <div>
                      <Label className="text-gray-400 text-sm mb-1 block">Required Plan(s)</Label>
                      <p className="text-gray-500 text-xs mb-2">Select which subscription plans grant access to this game. Leave empty to allow any active subscriber.</p>
                      <div className="space-y-1">
                        {(subPlans || []).map(p => (
                          <label key={p.id} className="flex items-center gap-2 p-2 rounded bg-gray-800/50 hover:bg-gray-800 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.required_plan_ids || []).includes(p.id)}
                              onChange={(e) => {
                                const ids = formData.required_plan_ids || [];
                                setFormData({
                                  ...formData,
                                  required_plan_ids: e.target.checked ? [...ids, p.id] : ids.filter(id => id !== p.id)
                                });
                              }}
                              className="rounded"
                            />
                            <span className="text-white text-sm">{p.name}</span>
                            <span className="text-gray-500 text-xs ml-auto">${p.price}/{p.interval}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PPV Price */}
                  {formData.access_type === 'ppv' && (
                    <div>
                      <Label className="text-gray-400 text-sm">PPV Price</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.ppv_price || ''}
                        onChange={(e) => setFormData({ ...formData, ppv_price: e.target.value ? parseFloat(e.target.value) : null })}
                        className="bg-gray-700 border-gray-600 text-white w-40 mt-1"
                        placeholder="e.g. 11.99"
                        data-testid="game-ppv-price"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Channel Assignment */}
              {editingGame && (
                <div className="border border-gray-700 rounded-lg p-4">
                  <Label className="text-gray-300 text-lg mb-3 block flex items-center gap-2">
                    <Radio className="w-5 h-5 text-sky-400" /> Streaming Channel
                  </Label>
                  {editingGame.channel_id ? (
                    <div className="bg-black/30 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-green-400 text-sm font-medium">
                            {channels.find(c => c.id === editingGame.channel_id)?.name || 'Channel assigned'}
                          </span>
                          <p className="text-gray-500 text-xs mt-0.5">Stream key is shared from the channel — no per-game key needed</p>
                        </div>
                        <Button size="sm" variant="ghost" className="text-orange-400 hover:text-orange-300 text-xs"
                          onClick={async () => {
                            const ch = channels.find(c => c.id === editingGame.channel_id);
                            if (!ch) return;
                            if (!window.confirm('Unassign this channel from the game?')) return;
                            try {
                              await api.unassignChannel(ch.id);
                              onRefresh();
                              const res = await api.getChannels();
                              setChannels(res.data);
                            } catch {}
                          }}>
                          Unassign
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Select
                        value=""
                        onValueChange={async (channelId) => {
                          if (!channelId) return;
                          setAssigningChannel(true);
                          try {
                            const res = await api.assignChannel(channelId, { game_id: editingGame.id });
                            if (res.data.conflict) {
                              const ok = window.confirm(
                                `This channel is currently assigned to "${res.data.conflict.title}" (${res.data.conflict.status}). Reassign it? The current game will be marked as finished.`
                              );
                              if (ok) {
                                await api.forceAssignChannel(channelId, { game_id: editingGame.id });
                              } else {
                                setAssigningChannel(false);
                                return;
                              }
                            }
                            onRefresh();
                            const chRes = await api.getChannels();
                            setChannels(chRes.data);
                          } catch (e) {
                            alert(e.response?.data?.detail || 'Failed to assign channel');
                          } finally {
                            setAssigningChannel(false);
                          }
                        }}
                      >
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white" data-testid="game-channel-select">
                          <SelectValue placeholder={assigningChannel ? 'Assigning...' : 'Select a channel...'} />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {channels.filter(c => !c.current_game_id || c.current_game_id === editingGame.id).length === 0 ? (
                            <div className="text-gray-500 text-sm p-3">No available channels. Create one in the Channels tab.</div>
                          ) : (
                            channels.map(c => (
                              <SelectItem key={c.id} value={c.id} className="text-white">
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${c.current_game_id ? 'bg-amber-500' : 'bg-green-500'}`} />
                                  {c.name}
                                  {c.current_game_id && c.current_game_id !== editingGame.id && (
                                    <span className="text-gray-500 text-xs">(in use)</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-gray-500 text-xs">Assign a persistent channel to share its RTMP key with this game.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Mux Live Stream */}
              {editingGame && (
                <div className="border border-gray-700 rounded-lg p-4">
                  <Label className="text-gray-300 text-lg mb-3 block">Live Streaming (Mux)</Label>
                  <StreamPanel game={editingGame} onUpdate={onRefresh} />
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400">Save Game</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={closeForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {games.map(game => (
          <Card key={game.id} className="bg-gray-800/50 border-sky-900/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Badge className={getStatusColor(game.status)}>{game.status.toUpperCase()}</Badge>
                  <div>
                    <h3 className="text-white font-semibold">
                      {game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}
                    </h3>
                    <p className="text-sm text-sky-200">{game.sport_name} / {game.league_name}</p>
                    <p className="text-sm text-gray-400">{formatDate(game.scheduled_time)} at {formatTime(game.scheduled_time)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {game.video_sources?.length > 0 && (
                    <div className="flex gap-1">
                      {game.video_sources.map((vs, i) => (
                        <Badge key={i} variant="outline" className="border-sky-400 text-sky-100">
                          {vs.type}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {game.mux_live_stream_id && (
                    <Badge className={`text-xs text-white ${game.mux_stream_status === 'active' ? 'bg-red-500 animate-pulse' : game.mux_stream_status === 'idle' ? 'bg-yellow-600' : 'bg-gray-600'}`}>
                      MUX: {(game.mux_stream_status || 'configured').toUpperCase()}
                    </Badge>
                  )}
                  {game.is_paywall && (
                    <Badge className="bg-purple-600 text-white text-xs">PAYWALL</Badge>
                  )}
                  {(game.home_score !== null || game.away_score !== null) && (
                    <div className="text-2xl font-bold text-white">
                      {game.home_score ?? '-'} - {game.away_score ?? '-'}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEdit(game)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(game.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {games.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No games yet. Add teams first, then add games!
          </div>
        )}
      </div>
    </div>
  );
};

export { AdminGames };
